import { useEffect } from "react";

/** Each screen names itself in the browser tab (WCAG 2.4.2). `full` uses the title as given, without "· LawSim". */
export function useDocumentTitle(title: string, opts: { full?: boolean } = {}): void {
  const full = opts.full ?? false;
  useEffect(() => {
    document.title = full ? title : title ? `${title} · LawSim` : "LawSim";
  }, [title, full]);
}

/**
 * Moves focus to the first heading inside `root` (made focusable with tabindex=-1 if it is not already),
 * so a screen-reader user hears where they have arrived. Falls back to `root` itself. Returns whether it moved.
 */
export function focusFirstHeading(root: HTMLElement | null, opts: { preventScroll?: boolean } = {}): boolean {
  if (!root) return false;
  const h = root.querySelector<HTMLElement>("h1, h2, h3");
  const target = h ?? root;
  if (!target.hasAttribute("tabindex") && !isFocusable(target)) target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: opts.preventScroll ?? false });
  return document.activeElement === target;
}

function isFocusable(el: HTMLElement): boolean {
  return el.matches("a[href], button, input, select, textarea, [tabindex]");
}

/** True when focus has been lost to the page, typically because the focused control was removed. */
export function focusIsLost(): boolean {
  const a = document.activeElement;
  return !a || a === document.body || a === document.documentElement || !a.isConnected;
}
